import {getTestProps} from '@benchling/js-test-utils';
import {StyleSheet, css} from 'aphrodite';
import React, {useMemo} from 'react';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatMessageThread} from 'llm/chat-library/ChatMessageThread';
import DataCaptureChat from 'llm/data-capture/DataCaptureChat';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import ChatScrollContainer from '../ChatScrollContainer';
import {ProgressStatus} from '../messages/MessageTypes';
import {createProgressMessage} from '../messages/MessageUtils';
import {useChatSessionEvents} from '../useChatSessionEvents';

interface ReadOnlyChatProps {
  chatId: string;
  isExpanded: boolean;
}

export function ReadOnlyChat(props: ReadOnlyChatProps): JSX.Element {
  const {chatId, isExpanded} = props;

  const {
    events: serverEvents,
    isLoading,
    errorMessage,
    chatType,
  } = useChatSessionEvents({
    chatSessionId: chatId,
    // Tools calls are not supported for read-only.
    includeToolCalls: false,
  });

  const events = useMemo(
    () => [
      ...serverEvents,
      ...(errorMessage
        ? [
            createProgressMessage({
              status: ProgressStatus.FAILED,
              errorMessage,
              agentType: null,
              progressData: null,
            }),
          ]
        : []),
    ],
    [serverEvents, errorMessage]
  );

  const footerMessage = (
    <div className={css(styles.footerMessage)}>
      <p>This is a read-only chat. To send a message, please create a new chat.</p>
    </div>
  );

  if (chatType === ChatType.DATA_CAPTURE) {
    return (
      <div className={css(styles.contentContainer)}>
        <DataCaptureChat chatSessionId={chatId} isExpanded={isExpanded} locked={true} />
        {footerMessage}
      </div>
    );
  }

  return (
    <ChatScrollContainer
      autoScroll={false}
      chatMessagesElement={
        <>
          {isLoading ? (
            <CenteredHorizontalThrobber
              {...getTestProps({component: 'CenteredHorizontalThrobber', element: 'root'})}
            />
          ) : (
            <ChatMessageThread
              events={events}
              actionButton={null}
              readonly={true}
              chatSessionId={chatId}
              progressData={null}
            />
          )}
          {footerMessage}
        </>
      }
      chatInputElement={null}
      latestUserMessageInfo={null}
    />
  );
}

const styles = StyleSheet.create({
  footerMessage: {
    fontSize: '10px',
    textAlign: 'center',
    marginTop: '4px',
    marginBottom: '4px',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
});

export default ReadOnlyChat;
